import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createShop, fetchAdminOrganizationBySlug } from "../api.js";
import { Button } from "../components/ui/button.jsx";
import { ArrowLeft } from "lucide-react";

export default function ShopCreatePage() {
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    timezone: "Europe/Lisbon",
    address: "",
    contact_email: "",
    contact_phone: "",
    min_advance_hours: 0,
  });
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const orgData = await fetchAdminOrganizationBySlug(orgSlug);
        if (!mounted) return;
        setOrg(orgData);
      } catch (err) {
        setError(err.response?.data?.detail || "Erro ao carregar organizacao");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [orgSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const shop = await createShop({ ...form, org_id: org?.id });
      navigate(`/admin/${orgSlug}/shops/${shop.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao criar salão");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-background p-6 md:p-12">
        <p className="text-white text-center">A carregar…</p>
      </main>
    );
  }

  if (!org || error) {
    return (
      <main className="min-h-screen bg-background p-6 md:p-12">
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg max-w-2xl mx-auto">
          {error || "Organizacao nao encontrada"}
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background p-6 md:p-12">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(`/admin/${orgSlug}`)}
          className="flex items-center gap-2 text-white mb-6 hover:text-gold transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar</span>
        </button>

        <header className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gold mb-3">
            Novo salão
          </h1>
        </header>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="bg-card border border-border rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="shop-name" className="block text-sm font-medium text-white">
                Nome *
              </label>
              <input
                id="shop-name"
                value={form.name}
                onChange={set("name")}
                required
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="shop-address" className="block text-sm font-medium text-white">
                Morada *
              </label>
              <input
                id="shop-address"
                value={form.address}
                onChange={set("address")}
                required
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="shop-email" className="block text-sm font-medium text-white">
                Email de contacto
              </label>
              <input
                id="shop-email"
                type="email"
                value={form.contact_email}
                onChange={set("contact_email")}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="shop-phone" className="block text-sm font-medium text-white">
                Telefone de contacto
              </label>
              <input
                id="shop-phone"
                value={form.contact_phone}
                onChange={set("contact_phone")}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="shop-advance-hours" className="block text-sm font-medium text-white">
                Horas mínimas de antecedência das reservas
              </label>
              <input
                id="shop-advance-hours"
                type="number"
                min="0"
                value={form.min_advance_hours}
                onChange={(e) => setForm({ ...form, min_advance_hours: parseInt(e.target.value, 10) || 0 })}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1" disabled={submitting}>
                {submitting ? "A criar…" : "Criar salão"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/admin/${orgSlug}`)}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
